
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { X, Search, Plus } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

interface RelatedGamesSelectorProps {
  selectedGameIds: string[];
  onChange: (gameIds: string[]) => void;
}

const RelatedGamesSelector: React.FC<RelatedGamesSelectorProps> = ({ 
  selectedGameIds, 
  onChange 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: games, isLoading } = useQuery({
    queryKey: ['games-search', debouncedSearch],
    queryFn: async () => {
      let query = supabase
        .from('games')
        .select('id, title, thumbnail, category')
        .eq('hidden', false)
        .order('title');

      if (debouncedSearch) {
        query = query.ilike('title', `%${debouncedSearch}%`);
      }

      const { data, error } = await query.limit(20);
      if (error) throw error;
      return data;
    },
    enabled: true,
  });

  const { data: selectedGames } = useQuery({
    queryKey: ['selected-games', selectedGameIds],
    queryFn: async () => {
      if (selectedGameIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('games')
        .select('id, title, thumbnail, category')
        .in('id', selectedGameIds);
      
      if (error) throw error;
      return data;
    },
    enabled: selectedGameIds.length > 0,
  });

  const handleAddGame = (gameId: string) => {
    if (!selectedGameIds.includes(gameId)) {
      onChange([...selectedGameIds, gameId]);
    }
  };

  const handleRemoveGame = (gameId: string) => {
    onChange(selectedGameIds.filter(id => id !== gameId));
  };

  return (
    <div className="space-y-4">
      <Label>Related Games</Label>
      
      {/* Selected Games */}
      {selectedGames && selectedGames.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-gray-600">Selected Games ({selectedGames.length})</Label>
          <div className="flex flex-wrap gap-2">
            {selectedGames.map((game) => (
              <Badge
                key={game.id}
                variant="secondary"
                className="flex items-center gap-2 px-3 py-1"
              >
                <img 
                  src={game.thumbnail || '/placeholder.svg'} 
                  alt={game.title}
                  className="w-4 h-4 rounded object-cover"
                />
                <span className="text-sm">{game.title}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-gray-200"
                  onClick={() => handleRemoveGame(game.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Search Games */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search games to add..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Game Results */}
        {searchTerm && (
          <Card className="max-h-60 overflow-y-auto">
            <CardContent className="p-2">
              {isLoading ? (
                <div className="text-center py-4 text-gray-500">Searching...</div>
              ) : games && games.length > 0 ? (
                <div className="space-y-1">
                  {games.map((game) => (
                    <div
                      key={game.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                    >
                      <div className="flex items-center gap-3">
                        <img 
                          src={game.thumbnail || '/placeholder.svg'} 
                          alt={game.title}
                          className="w-8 h-8 rounded object-cover"
                        />
                        <div>
                          <div className="text-sm font-medium">{game.title}</div>
                          <div className="text-xs text-gray-500">{game.category}</div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddGame(game.id)}
                        disabled={selectedGameIds.includes(game.id)}
                      >
                        {selectedGameIds.includes(game.id) ? 'Added' : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">No games found</div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RelatedGamesSelector;
